import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button, Input, Card, Form, Alert, message, TreeSelect } from 'antd'
import {
  CheckCircleOutlined,
  DownOutlined,
  HistoryOutlined,
  LoadingOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  TeamOutlined,
  UpOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useAppSelector, useAppDispatch } from '../../../app/hooks'
import {
  getCreditHistory,
  HistoryItem,
  addCreditHistory
} from '../utils/creditHistory'
import { fetchMaskedName, fetchCreditReport } from '../../../actions/credit'
import { fetchGroupDeparment } from '../../../actions/group'
import ConfirmIdentityModal from '../components/ConfirmIdentityModal'
import ResultCard from '../components/ResultCard'
import { CreditRecord } from '../components/ResultCard'
import { CREDIT_STANDARDS, CreditLevel } from '../constants/creditStandards'
import { TECH_FEATURES } from '../constants/techFeatures'
import parseQueryString from '../../../utils/parseQueryString'
import sensors from '../../../utils/sensors'
import './index.scss'
import CONFIG from '../../../constants/config'

import PageHeader from '@/components/PageHeader'
export default function Home() {
  const [phone, setPhone] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [error, setError] = useState('')
  const [queryHistory, setQueryHistory] = useState<HistoryItem[]>([])
  const [confirmModalVisible, setConfirmModalVisible] = useState(false)
  const [maskedName, setMaskedName] = useState('')
  const [currentQueryPhone, setCurrentQueryPhone] = useState('') // 当前正在查询的手机号
  const [loadingMaskedName, setLoadingMaskedName] = useState(false)
  const [loadingCreditReport, setLoadingCreditReport] = useState(false) // 查询信用报告的加载状态
  const [nameVerifyError, setNameVerifyError] = useState(false) // 名字校验失败的错误状态
  const [departmentTree, setDepartmentTree] = useState<any[]>([]) // 部门树数据
  const [selectedMemberId, setSelectedMemberId] = useState<string | undefined>() // 选中的成员ID
  const [result, setResult] = useState<CreditRecord | null>(null) // 查询结果
  const [isSearching, setIsSearching] = useState(false) // 搜索状态
  const [showMemberSelect, setShowMemberSelect] = useState(false) // 是否显示团队成员选择器
  const [treeSelectOpen, setTreeSelectOpen] = useState(false) // TreeSelect下拉框是否打开
  const memberSelectContainerRef = useRef<HTMLDivElement>(null) // TreeSelect容器引用
  const pendingOpenRef = useRef(false)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const group = useAppSelector((state) => state.group)

  const trackEvent = (params: {
    actionType: string
    photoNum?: string
    number1?: number
  }) => {
    sensors.track('workgroup_b7847_completed', {
      requirement_id: 871,
      track_sign: 'aworkgroup.b7847.completed.workgroup_b7847_completed',
      actionType: params.actionType,
      number1: params.number1,
      photoNum: params.photoNum,
      role: group.myRole?.toString() || '',
      groupID: group.groupId?.toString() || ''
    })
  }

  useEffect(() => {
    trackEvent({
      actionType: '进入页面'
    })
  }, [])

  useEffect(() => {
    const history = getCreditHistory()
    setQueryHistory(history)
  }, [])

  useEffect(() => {
    fetchDepartmentTree()
  }, [])

  const fetchDepartmentTree = async () => {
    const groupID = group.groupId
    if (!groupID) return

    try {
      const result: any = await dispatch(fetchGroupDeparment({ groupID }))
      // 接口返回的是 result.data.departmentTree，且是对象而不是数组
      const departmentTreeData =
        result?.data?.departmentTree || result?.departmentTree
      if (departmentTreeData) {
        // 将单个对象转换为数组格式，因为 TreeSelect 需要数组
        const treeData = transformDepartmentTree([departmentTreeData])
        console.log('部门树数据:', treeData) // 调试日志
        setDepartmentTree(treeData)
      } else {
        console.warn('部门树数据为空:', result)
      }
    } catch (err) {
      console.error('获取部门树失败:', err)
    }
  }

  const transformDepartmentTree = (data: any[]): any[] => {
    return data.map((item) => {
      const node: any = {
        title: item.name,
        value: item.id,
        key: item.id,
        selectable: false // 部门节点不可选择，只能选择成员
      }

      // 先处理子部门
      if (item.children && item.children.length > 0) {
        node.children = transformDepartmentTree(item.children)
      }

      // 再处理成员（成员应该添加到 children 中）
      // 注意：接口返回的字段是 userID, mobile, nickname, headImgUrl
      if (item.users && item.users.length > 0) {
        node.children = node.children || []
        item.users.forEach((user: any) => {
          // 过滤掉 mobile 为空或空字符串的成员
          if (!user.mobile || user.mobile.trim() === '') {
            return
          }
          // 已退出成员：userStatus 表示已退出（具体枚举值按接口约定）
          const isQuit = user.userStatus === CONFIG.memberStatus.QUITED
          const displayName = isQuit
            ? `${user.nickname || ''}（已退出）`
            : user.nickname || ''

          node.children.push({
            title: (
              <span
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <img
                  src={user.headImgUrl}
                  alt={user.nickname}
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    flexShrink: 0
                  }}
                  onError={(e) => {}}
                />
                <span>{displayName}</span>
              </span>
            ),
            // 用于搜索的文本（TreeSelect 的 treeNodeFilterProp 会使用这个）
            searchText: displayName,
            value: `user_${user.userID}`,
            key: `user_${user.userID}`,
            isLeaf: true,
            selectable: true, // 成员节点可选择
            phone: user.mobile, // 保存手机号用于后续查询
            userID: user.userID, // 保存用户ID
            nickname: user.nickname // 保存昵称
          })
        })
      }

      return node
    })
  }

  const handleMemberSelect = (value: string, node: any) => {
    if (value && value.startsWith('user_')) {
      // 从 node 中获取 phone，TreeSelect 的 node 结构可能是 node.props 或直接包含数据
      const phone =
        node?.phone ||
        node?.props?.phone ||
        node?.data?.phone ||
        node?.node?.props?.phone ||
        node?.node?.phone

      if (phone) {
        // 直接调用查询接口，不填充输入框
        setSelectedMemberId(value)
        setError('')
        setShowMemberSelect(false) // 选择成员后关闭TreeSelect
        setTreeSelectOpen(false)
        handleQueryMaskedName(phone)

        // 埋点上报
        trackEvent({
          actionType: '点击成员',
          photoNum: phone
        })
      } else {
        console.warn('无法获取成员手机号:', node)
        message.error('无法获取成员信息，请重试')
      }
    }
  }

  const handleMemberChange = (value: string) => {
    if (!value) {
      setSelectedMemberId(undefined)
    }
  }

  const handleToggleTeamSelect = () => {
    if (treeSelectOpen || showMemberSelect) {
      pendingOpenRef.current = false
      setTreeSelectOpen(false)
      setTimeout(() => setShowMemberSelect(false), 200)
      return
    }

    pendingOpenRef.current = true
    setShowMemberSelect(true)
  }

  useEffect(() => {
    if (showMemberSelect && pendingOpenRef.current) {
      pendingOpenRef.current = false
      setTreeSelectOpen(true)
      // 滚动到TreeSelect容器位置，确保完全可见
      setTimeout(() => {
        if (memberSelectContainerRef.current) {
          const element = memberSelectContainerRef.current
          const elementPosition =
            element.getBoundingClientRect().top + window.pageYOffset
          const offsetPosition = elementPosition - 300 // 向上滚动300px，确保有足够空间显示下拉框

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
        }
      }, 100)
    }
  }, [showMemberSelect])

  /**
   * 查询脱敏姓名（需要在 useEffect 之前定义，以便在 useEffect 中使用）
   */
  const handleQueryMaskedName = async (
    queryPhone: string,
    showError = false
  ) => {
    setCurrentQueryPhone(queryPhone) // 保存当前查询的手机号
    setLoadingMaskedName(true)
    setIsSearching(true)
    if (showError) {
      setNameVerifyError(true)
    }
    try {
      const groupID = group.groupId
      const requestParams: any = { phone: queryPhone }
      if (groupID) {
        requestParams.groupID = groupID
      }
      const result: any = await dispatch(fetchMaskedName(requestParams))

      if (result && result.code === 200 && result.data) {
        // 检查错误码 - 不存在，则返回错误码1001
        if (result.data.status === 1001) {
          message.error(result.data.msg || '用户不存在')
          trackEvent({
            actionType: '查询失败',
            photoNum: queryPhone
          })
          return
        }

        // 检查错误码 - 已注销，则返回错误码1002
        if (result.data.status === 1002) {
          message.error(result.data.msg || '用户已注销')
          trackEvent({
            actionType: '查询失败',
            photoNum: queryPhone
          })
          return
        }

        // 如果是团队内成员，直接调用查询接口（不需要弹窗确认）
        if (result.data.needValidate === false) {
          handleQueryCreditReport(queryPhone)
          return
        }

        // 如果没有姓名（userNameMask 为空），直接调用查询接口
        if (!result.data.userNameMask) {
          handleQueryCreditReport(queryPhone)
          return
        }

        // 团队外成员，需要弹窗确认
        setMaskedName(result.data.userNameMask)
        setConfirmModalVisible(true)
        trackEvent({
          actionType: '校验弹窗出现',
          photoNum: queryPhone
        })
        // 如果有名字校验失败的错误，设置错误状态
        if (showError) {
          setNameVerifyError(true)
        }
      } else {
        message.error(
          result?.msg || result?.toast_msg || '查询失败，请稍后重试'
        )
      }
    } catch (err) {
      console.error('获取脱敏姓名失败:', err)
      message.error('查询失败，请稍后重试')
    } finally {
      setLoadingMaskedName(false)
      setIsSearching(false)
    }
  }

  const handleSubmit = () => {
    handleSearch()
  }

  const handleSearch = () => {
    setError('')
    setResult(null)

    const phoneRegex = /^1[3-9]\d{9}$/
    const searchPhone = phoneNumber || phone

    if (!searchPhone.trim()) {
      setError('请输入手机号')
      return
    }
    if (!phoneRegex.test(searchPhone)) {
      setError('请输入有效的手机号')
      return
    }

    trackEvent({
      actionType: '点击查询',
      photoNum: searchPhone
    })

    // 先调用 masked_name 接口
    handleQueryMaskedName(searchPhone)
  }

  const handleHistoryClick = (historyPhone: string) => {
    setPhone(historyPhone)
    setPhoneNumber(historyPhone)

    trackEvent({
      actionType: '点击一条历史记录',
      photoNum: historyPhone
    })

    // 先调用 masked_name 接口
    handleQueryMaskedName(historyPhone)
  }

  /**
   * 查询信用报告
   */
  const handleQueryCreditReport = async (
    queryPhone: string,
    username?: string
  ) => {
    setLoadingCreditReport(true)
    setIsSearching(true)
    try {
      const groupID = group.groupId
      const requestParams: any = { phone: queryPhone }

      if (username) {
        requestParams.username = username
      }

      if (groupID) {
        requestParams.groupID = groupID
      }

      const result: any = await dispatch(fetchCreditReport(requestParams))

      if (result && result.code === 200 && result.data) {
        // 错误码 1001：用户不存在
        if (result.data.status === 1001) {
          message.error(result.data.msg || '用户不存在')
          setResult(null)
          trackEvent({
            actionType: '查询失败',
            photoNum: queryPhone
          })
          return false
        }

        // 错误码 1002：账号已注销
        if (result.data.status === 1002) {
          message.error(result.data.msg || '账号已注销')
          setResult(null)
          trackEvent({
            actionType: '查询失败',
            photoNum: queryPhone
          })
          return false
        }

        // 错误码 1003：名字校验不通过
        if (result.data.status === 1003) {
          // 名字校验失败时，显示错误提示并设置错误状态
          // 注意：不在这里显示 message.error，因为 ConfirmIdentityModal 组件会显示
          setResult(null)
          trackEvent({
            actionType: '查询失败',
            photoNum: queryPhone
          })
          // 设置错误状态，弹窗会显示错误提示并让输入框变红
          setNameVerifyError(true)
          // 确保弹窗是打开状态
          setConfirmModalVisible(true)
          return false
        }

        // 查询成功，添加历史记录
        addCreditHistory(queryPhone)

        // 处理并映射数据
        const apiData = result.data
        const mappedData: CreditRecord = {
          userName: apiData.userName || '未知用户',
          userAvatar: apiData.userAvatar || '',
          telNum: apiData.telNum || queryPhone,
          registerTime: apiData.registerTime || '',
          preferLocation: apiData.preferLocation || '暂无数据',
          creditScore: apiData.creditScore || 0,
          creditRating: apiData.creditRating || '新注册',
          reportTime:
            apiData.reportTime ||
            apiData.versionTime ||
            new Date().toLocaleString('zh-CN'),
          activeDays: apiData.activeDays || 0,
          highAbnormalCnt: apiData.highAbnormalCnt || 0,
          sumHighAbnormalCnt: apiData.sumHighAbnormalCnt || 0,
          lowAbnormalCnt: apiData.lowAbnormalCnt || 0,
          midAbnormalCnt: apiData.midAbnormalCnt || 0,
          lastSumHighAbnormalTime: apiData.lastSumHighAbnormalTime || '无',
          level: apiData.level || 0
        }

        // 显示查询成功提示
        message.success('查询成功')

        // 更新历史记录显示
        const queryHistoryList = getCreditHistory()
        setQueryHistory(queryHistoryList)

        trackEvent({
          actionType: '查询成功',
          number1: mappedData.creditScore,
          photoNum: queryPhone
        })

        // 直接跳转到报告页，通过 state 传递数据
        const reportUrl = username
          ? `/credit/report/${queryPhone}?username=${encodeURIComponent(
              username
            )}`
          : `/credit/report/${queryPhone}`

        // 跳转前滚动到页面顶部
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })

        // 延迟跳转，确保滚动动画完成
        setTimeout(() => {
          navigate(reportUrl, { state: { creditData: mappedData } })
        }, 100)

        return true
      } else {
        setResult(null)
        message.error(
          result?.msg || result?.toast_msg || '查询失败，请稍后重试'
        )
        trackEvent({
          actionType: '查询失败',
          photoNum: queryPhone
        })
        return false
      }
    } catch (err) {
      console.error('获取信用报告失败:', err)
      setResult(null)
      message.error('查询失败，请稍后重试')
      trackEvent({
        actionType: '查询失败',
        photoNum: queryPhone
      })
      return false
    } finally {
      setLoadingCreditReport(false)
      setIsSearching(false)
    }
  }

  /**
   * 确认身份后跳转到报告页
   */
  const handleConfirmIdentity = async (username: string) => {
    // 使用当前查询的手机号
    const queryPhone = currentQueryPhone || phone

    trackEvent({
      actionType: '点击核实',
      photoNum: queryPhone
    })

    // 调用查询信用报告接口
    const success = await handleQueryCreditReport(queryPhone, username)

    if (success) {
      // 查询成功，关闭弹窗并重置状态
      setConfirmModalVisible(false)
      setMaskedName('')
      setCurrentQueryPhone('')
      setNameVerifyError(false)
    } else {
      // 查询失败（可能是名字校验失败），保持弹窗打开并设置错误状态
      // handleQueryCreditReport 内部已经处理了错误状态设置
      // 不需要关闭弹窗，让用户重新输入
    }
  }

  /**
   * 取消确认弹窗：关闭时清空 TreeSelect 选中
   */
  const handleCancelConfirm = () => {
    setConfirmModalVisible(false)
    setMaskedName('')
    setCurrentQueryPhone('')
    setNameVerifyError(false)
    setSelectedMemberId(undefined) // 清空团队列表选中
  }

  return (
    <div className="credit-home-container">
      <PageHeader
        ghost={false}
        style={{
          borderBottom: '1px solid rgb(235, 237, 240)',
          overflow: 'visible'
        }}
        title={
          <div
            style={{
              flex: 1,
              flexDirection: 'column',
              gap: '10px'
            }}
          >
            <div
              style={{
                background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 'bold',
                fontSize: '18px',
                lineHeight: '30px'
              }}
            >
              工作信用查询系统
            </div>
            <div
              style={{
                fontSize: '10px',
                color: '#64748b',
                fontWeight: 'normal',
                lineHeight: '10px'
              }}
            >
              今日水印相机 · 真实时间认证 · 权威可信
            </div>
          </div>
        }
      />

      <main className="credit-main-new">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-background"></div>
          <h2 className="hero-title">
            <span className="gradient-text">工作信用</span>查询
          </h2>
          <p className="hero-description">
            基于中科院授时中心、区块链存证及AI智能算法，为您提供最真实的职场信用凭证，让每一份努力都被权威铭记。
          </p>

          <div className="search-container">
            <div className="search-wrapper">
              <div className="search-input-wrapper">
                <div className="search-icon">
                  <SearchOutlined />
                </div>
                <input
                  type="tel"
                  maxLength={11}
                  placeholder="请输入11位手机号"
                  className="search-input"
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value)
                    setPhone(e.target.value)
                    setError('')
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button
                  type="button"
                  onClick={handleToggleTeamSelect}
                  className="search-team-button"
                >
                  <TeamOutlined />
                  <span>查看团队成员</span>
                  {treeSelectOpen ? <UpOutlined /> : <DownOutlined />}
                </button>
                <button
                  onClick={handleSearch}
                  disabled={isSearching}
                  className="search-button"
                >
                  {isSearching ? <LoadingOutlined spin /> : '立即查询'}
                </button>
              </div>
            </div>
            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                className="search-error"
              />
            )}
            {showMemberSelect && (
              <div
                className="member-select-container"
                ref={memberSelectContainerRef}
              >
                <TreeSelect
                  className="credit-member-tree-select"
                  style={{
                    width: '100%',
                    maxWidth: '42rem',
                    height: 0,
                    overflow: 'visible'
                  }}
                  value={selectedMemberId}
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  placeholder="查询团队成员"
                  allowClear
                  treeDefaultExpandAll={true}
                  treeData={departmentTree}
                  onChange={handleMemberChange}
                  onSelect={handleMemberSelect}
                  open={treeSelectOpen}
                  onDropdownVisibleChange={(open) => {
                    if (open) {
                      setTreeSelectOpen(true)
                      trackEvent({ actionType: '点击组织架构' })
                      return
                    }
                    if (pendingOpenRef.current) {
                      return
                    }
                    setTreeSelectOpen(false)
                    // 当下拉框关闭时，延迟隐藏容器
                    setTimeout(() => setShowMemberSelect(false), 200)
                  }}
                  showSearch
                  treeNodeFilterProp="searchText"
                  treeCheckable={false}
                  autoFocus
                />
              </div>
            )}
          </div>

          <div className="hero-features">
            <div className="hero-feature-item">
              <CheckCircleOutlined className="feature-icon" />
              <span>中科院授时中心授权</span>
            </div>
            <div className="hero-feature-item">
              <CheckCircleOutlined className="feature-icon" />
              <span>区块链端对端加密</span>
            </div>
            <div className="hero-feature-item">
              <CheckCircleOutlined className="feature-icon" />
              <span>司法鉴定全网采信</span>
            </div>
          </div>
        </section>

        {/* Credit Standards Section */}
        <section className="standards-section">
          <div className="standards-header">
            <div>
              <h3 className="standards-title">
                <SafetyCertificateOutlined className="standards-icon" />
                信用等级标准
              </h3>
              <p className="standards-subtitle">
                公开透明的信用评价体系，确保评估客观公正
              </p>
            </div>
          </div>
          <div className="standards-grid">
            {CREDIT_STANDARDS.map((standard) => (
              <div key={standard.level} className="standard-card">
                <div
                  className={`standard-icon-wrapper standard-icon-${standard.color}`}
                >
                  {standard.icon}
                </div>
                <h4 className="standard-card-title">{standard.title}</h4>
                <p className="standard-card-description">
                  {standard.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Core Tech Section */}
        <section className="tech-section">
          <div className="tech-header">
            <span className="tech-label">Technology</span>
            <h3 className="tech-title">核心验真技术</h3>
            <p className="tech-subtitle">
              基于区块链与国家授时中心技术的照片防篡改、防造假闭环验证体系
            </p>
          </div>
          <div className="tech-grid">
            {TECH_FEATURES.map((tech) => (
              <div key={tech.id} className="tech-item">
                <div className="tech-item-number">NO.{tech.id}</div>
                <h4 className="tech-item-title">{tech.title}</h4>
                <p className="tech-item-description">{tech.description}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Recent Queries & Footer Branding - Full Width */}
      <div className="history-section">
        <div className="history-content">
          <h4 className="history-title">
            <HistoryOutlined className="history-icon" />
            最近查询动态
          </h4>
          <div className="history-list">
            {queryHistory && queryHistory.length > 0 ? (
              queryHistory.map((item, idx) => (
                <div
                  key={idx}
                  className="history-item"
                  onClick={() => handleHistoryClick(item.phone)}
                >
                  <div className="history-item-left">
                    <div className="history-dot"></div>
                    <span className="history-phone">
                      {item.phone.slice(0, 3)}****{item.phone.slice(-4)}
                    </span>
                  </div>
                  <div className="history-time">
                    {dayjs(item.queriedAt).format('MM-DD HH:mm')}
                  </div>
                </div>
              ))
            ) : (
              <div className="history-empty">暂无查询记录</div>
            )}
          </div>
        </div>
      </div>

      <ConfirmIdentityModal
        visible={confirmModalVisible}
        maskedName={maskedName}
        phone={currentQueryPhone || phone}
        onConfirm={handleConfirmIdentity}
        onCancel={handleCancelConfirm}
        loading={loadingMaskedName || loadingCreditReport}
        hasError={nameVerifyError}
        onErrorDismiss={() => setNameVerifyError(false)}
      />

      <footer className="credit-footer">
        <div className="credit-footer-content">
          <p>今日水印相机 · 工作信用查询系统</p>
          <p className="credit-footer-subtitle">
            3亿人都在用的工作相机 · 已覆盖100多个行业
          </p>
        </div>
      </footer>
    </div>
  )
}
